import { useCallback, useEffect, useState } from "react";
import { useChampionship } from "@/context/ChampionshipContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { countryFlagUrl } from "@/lib/countries";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Trash2, Plus } from "lucide-react";

const blankPlayer = { name: "", email: "", photo_path: "", is_male: false, is_female: false, is_youth: false };

export default function CaptainDashboardPage() {
  const { current } = useChampionship() || {};
  const { user } = useAuth() || {};
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState(blankPlayer);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const cc = user?.country_code;

  const load = useCallback(async () => {
    if (!current?.id || !cc) return;
    const { data } = await api.get(`/championships/${current.id}/countries/${cc}/players`);
    setPlayers(data || []);
  }, [current?.id, cc]);

  useEffect(() => { load(); }, [load]);

  if (!user || user.role !== "captain") {
    return <div className="max-w-3xl mx-auto px-6 py-20">Only captains can access this page.</div>;
  }

  if (!user.approved) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="captain-pending">
        <div className="text-[11px] uppercase tracking-[0.3em] text-amber-600">Account pending</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter mt-3">Awaiting approval</h1>
        <p className="mt-4 text-muted-foreground">
          Your captain account has been created. Once the WOC organising committee approves you,
          you'll be able to enter your country's roster here. You will receive an email confirmation.
        </p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/players/${editingId}`, form);
        toast.success("Player updated");
      } else {
        await api.post(`/championships/${current.id}/countries/${cc}/players`, form);
        toast.success("Player added");
      }
      setForm(blankPlayer);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      email: p.email || "",
      photo_path: p.photo_path || "",
      is_male: !!p.is_male,
      is_female: !!p.is_female,
      is_youth: !!p.is_youth,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!confirm("Remove this player?")) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success("Player removed");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  let submitLabel;
  if (busy) submitLabel = "Saving…";
  else if (editingId) submitLabel = "Save changes";
  else submitLabel = "Add to roster";

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="captain-dashboard">
      <div className="flex items-center gap-4 border-b border-border pb-8 mb-12">
        <img src={countryFlagUrl(cc)} alt="" className="h-12 w-16 object-cover border border-border" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-primary">Captain dashboard</div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{user.country_name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Managing roster for WOC {current?.year} — {current?.location}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-6">
            {editingId ? "Edit player" : "Add player"}
          </h2>
          <form onSubmit={submit} className="space-y-5 border border-border p-6">
            <div className="space-y-2">
              <Label htmlFor="pname">Player name</Label>
              <Input id="pname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="player-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pemail">Player email <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="pemail"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="player@example.com"
                data-testid="player-email-input"
              />
            </div>
            <ImageUpload
              label="Player photo (optional)"
              value={form.photo_path}
              onChange={(v) => setForm({ ...form, photo_path: v })}
            />
            <div className="space-y-3 pt-2">
              <div className="text-sm font-medium">Player classifications</div>
              <FlagCheck label="Male" checked={form.is_male} onChange={(v) => setForm({ ...form, is_male: v })} testid="flag-male" />
              <FlagCheck label="Female" checked={form.is_female} onChange={(v) => setForm({ ...form, is_female: v })} testid="flag-female" />
              <FlagCheck label="Youth (under 18)" checked={form.is_youth} onChange={(v) => setForm({ ...form, is_youth: v })} testid="flag-youth" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-player">
                {submitLabel}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={() => { setEditingId(null); setForm(blankPlayer); }}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </div>

        <div className="lg:col-span-7">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-6">
            <span>Roster <span className="text-sm font-normal text-muted-foreground">({players.length})</span></span>
          </h2>
          {players.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-muted-foreground">
              <Plus className="h-6 w-6 mx-auto mb-2" />
              No players yet — add your first player on the left.
            </div>
          ) : (
            <>
              <div className="text-xs text-muted-foreground mb-3 border border-amber-200 bg-amber-50 px-3 py-2 rounded-sm" data-testid="captain-approval-notice">
                <strong>Note:</strong> Each player you add must be approved by the WOC organising committee before they appear on the public Teams page. Edits to a player will revert them to "pending" until re-approved.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {players.map((p) => (
                  <div key={p.id} className="flex items-center gap-4 border border-border p-3" data-testid={`player-row-${p.id}`}>
                    <div className="h-14 w-14 bg-neutral-100 border border-border overflow-hidden flex items-center justify-center flex-shrink-0">
                      {p.photo_path ? <img src={imageUrl(p.photo_path)} alt="" className="h-full w-full object-cover" /> : <div className="w-6 h-6 rounded-full othello-disc-dark" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{p.name}</span>
                        {p.approved ? (
                          <span className="text-[9px] uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm" data-testid={`player-status-${p.id}`}>Approved</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded-sm" data-testid={`player-status-${p.id}`}>Pending</span>
                        )}
                      </div>
                      <div className="flex gap-1 mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {p.is_male && <span>· Male</span>}
                        {p.is_female && <span>· Female</span>}
                        {p.is_youth && <span>· Youth</span>}
                      </div>
                    </div>
                    <button onClick={() => edit(p)} className="text-xs underline" data-testid={`edit-player-${p.id}`}>Edit</button>
                    <button onClick={() => remove(p.id)} className="text-destructive p-1" data-testid={`delete-player-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FlagCheck({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 text-sm cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testid} />
      {label}
    </label>
  );
}
